import { Component, inject, input } from '@angular/core';
import { UwIcon } from '../../ui/icon/uw-icon';
import { UwBadge } from '../../ui/badge/uw-badge';
import { UwSkeleton } from '../../ui/skeleton/uw-skeleton';
import { UwTooltip } from '../../directives/tooltip.directive';
import { UwAiTraceLink } from '../ai-trace-link/uw-ai-trace-link';
import { EvidenceStore } from '../../../core/stores/evidence.store';
import { AiFeedbackService } from '../../../core/services/ai-feedback.service';
import type { AiNarrative, NarrativeSegment } from '../../../core/models/ai.model';
import type { EvidenceType } from '../../../core/models/evidence.model';

const EVIDENCE_LABELS: Record<EvidenceType, { label: string; icon: string }> = {
  authority: { label: 'Authority Check', icon: 'shield' },
  pricing: { label: 'Pricing & Rating', icon: 'calculator' },
  loss: { label: 'Loss Experience', icon: 'file-text' },
  risk: { label: 'Risk Assessment', icon: 'gauge' },
  enrichment: { label: 'Data Enrichment', icon: 'database' },
};

@Component({
  selector: 'uw-ai-narrative',
  standalone: true,
  imports: [UwIcon, UwBadge, UwSkeleton, UwAiTraceLink, UwTooltip],
  template: `
    <div
      class="space-y-4"
      (mouseup)="onSelection()"
      (keyup.shift)="onSelection()"
      (keyup.control)="onSelection()"
      (keyup.meta)="onSelection()"
      (contextmenu)="onSelection()"
    >
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-1.5">
          <uw-icon name="sparkles" [size]="14" class="text-ai" />
          <h2 class="typo-title text-primary">AI Risk Synthesis</h2>
        </div>
        <uw-badge tone="ai" icon="bot">Generated by Copilot</uw-badge>
      </div>

      @if (loading()) {
        <div class="space-y-2.5 rounded-lg border border-line bg-surface-elevated p-4">
          <uw-skeleton height="13px" width="90%" />
          <uw-skeleton height="13px" width="96%" />
          <uw-skeleton height="13px" width="70%" />
          <uw-skeleton height="13px" width="88%" />
        </div>
      } @else if (narrative(); as narration) {
        <div class="rounded-lg border border-line bg-surface-elevated p-4">
          <div class="text-[13px] leading-7" role="article" aria-label="AI risk synthesis narrative">
            @for (segment of narration.segments; track $index) {
              @switch (segment.kind) {
                @case ('text') {
                  <span>{{ segment.text }}</span>
                }
                @case ('emphasis') {
                  <span class="mx-0.5 inline-flex items-center gap-1 rounded bg-warning-soft px-1.5 py-0.5 text-[12px] font-bold" [class]="emphasisClass(segment)">
                    <uw-icon [name]="emphasisIcon(segment)" [size]="11" />
                    {{ segment.text }}
                  </span>
                }
                @case ('entity') {
                  <button
                    type="button"
                    class="mx-0.5 inline-flex items-center gap-1 rounded border-b-2 border-ai/50 bg-ai-soft px-1.5 py-0.5 align-baseline text-[12px] font-semibold text-ai transition-colors hover:bg-ai hover:text-white"
                    (click)="openEvidence(segment.evidence)"
                    [uwTooltip]="'View evidence: ' + (segment.hint ?? EVIDENCE_LABELS[segment.evidence].label)"
                  >
                    <uw-icon [name]="EVIDENCE_LABELS[segment.evidence].icon" [size]="11" />
                    {{ segment.text }}
                  </button>
                }
                @case ('trace') {
                  <uw-ai-trace-link [sourceId]="segment.documentId" [sourceName]="sourceName(segment.documentId)" />
                }
              }
            }
          </div>
        </div>

        <p class="flex items-start gap-1.5 text-[10.5px] leading-snug text-text-muted">
          <uw-icon name="shield-check" [size]="12" class="mt-0.5 shrink-0" />
          Select any text to correct the Copilot. Every claim in this synthesis is traceable to a source document or evidence check.
        </p>
      }
    </div>
  `,
})
export class UwAiNarrative {
  protected readonly EVIDENCE_LABELS = EVIDENCE_LABELS;

  private readonly evidence = inject(EvidenceStore);
  private readonly feedback = inject(AiFeedbackService);

  readonly narrative = input<AiNarrative | null>(null);
  readonly loading = input(false);

  protected sourceName(documentId: string): string {
    return this.evidence.sources().find((s) => s.id === documentId)?.documentName ?? 'source document';
  }

  protected openEvidence(type: EvidenceType): void {
    this.evidence.selectEvidence(type);
  }

  protected emphasisClass(segment: Extract<NarrativeSegment, { kind: 'emphasis' }>): string {
    const tones: Record<string, string> = {
      warning: 'text-warning',
      danger: 'text-danger',
      success: 'text-success',
      info: 'text-info',
    };
    return tones[segment.tone ?? 'warning'] ?? 'text-warning';
  }

  protected emphasisIcon(segment: Extract<NarrativeSegment, { kind: 'emphasis' }>): string {
    const tones: Record<string, string> = {
      warning: 'alert-triangle',
      danger: 'alert-triangle',
      success: 'check-circle',
      info: 'info',
    };
    return tones[segment.tone ?? 'warning'] ?? 'alert-triangle';
  }

  protected onSelection(): void {
    const selection = window.getSelection();
    const text = selection?.toString().trim();
    if (!selection || selection.isCollapsed || !text || selection.rangeCount === 0) {
      this.feedback.clearSelection();
      return;
    }
    const rect = selection.getRangeAt(0).getBoundingClientRect();
    this.feedback.setSelection({
      text,
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    });
  }
}
